import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form from "antd/es/form";
import notification from "antd/es/notification";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import { FormItem, Modal, TextField } from "@mapmycustomers/ui";

import LockedEntityType from "@app/component/activity/LockedFormFields/LockedEntityType";
import { getBouncedEmailEntity, isBouncedEmailEntityEmailUpdating } from "@app/store/notification";
import { updateBouncedEmailEntityEmail } from "@app/store/notification/actions";
import { RootState } from "@app/store/rootReducer";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";
import { getEntityTypeDisplayName, getSourceObjectDisplayName } from "@app/util/ui";

import messages from "./messages";

interface FormValues {
  email: string;
}

interface UpdateEmailModalProps {
  label: string;
  onHide: () => void;
  required: boolean;
}

const UpdateEmailModal = ({
  entity,
  entityEmailUpdating,
  label,
  onHide,
  onUpdateEmail,
  required,
}: UpdateEmailModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const sourceObjectArgs = useSourceObjectMessageArgs(entity.entity, entity.sourceObject);

  const [form] = Form.useForm<FormValues>();

  const handleSaveClick = useCallback(() => {
    form.submit();
  }, [form]);

  const handleFormFinish = useCallback(
    ({ email }: FormValues) => {
      onUpdateEmail({
        email,
        onSuccess: () => {
          notification.success({
            message: intl.formatMessage(messages.entityUpdatedSuccessfullyMessage, {
              ...sourceObjectArgs,
              entityName: getEntityTypeDisplayName(intl, entity.entity),
            }),
          });
          onHide();
        },
      });
    },
    [intl, entity.entity, onHide, onUpdateEmail, sourceObjectArgs]
  );

  return (
    <Modal
      cancelButtonProps={{
        disabled: entityEmailUpdating,
      }}
      destroyOnClose={true}
      okButtonProps={{
        disabled: entityEmailUpdating,
        loading: entityEmailUpdating,
      }}
      okText={intl.formatMessage(messages.saveButtonLabel)}
      onCancel={onHide}
      onOk={handleSaveClick}
      open
      size="small"
      title={intl.formatMessage(messages.updateEmailTitle)}
      type="info"
    >
      <Form
        form={form}
        initialValues={{ email: entity.email }}
        layout="vertical"
        onFinish={handleFormFinish}
      >
        <Space direction="vertical" size="middle">
          <span>{intl.formatMessage(messages.updateEmailDescription)}</span>
          <LockedEntityType
            entityType={entity.entity}
            label={getSourceObjectDisplayName(intl, entity.entity, sourceObjectArgs)}
            placeholder={entity.name}
          />
          <FormItem
            label={label}
            name="email"
            required={required}
            rules={[{ required, type: "email" }]}
          >
            <TextField addonBefore={<FontAwesomeIcon icon={faEnvelope} />} />
          </FormItem>
        </Space>
      </Form>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  entity: getBouncedEmailEntity(state)!,
  entityEmailUpdating: isBouncedEmailEntityEmailUpdating(state)!,
});

const mapDispatchToProps = {
  onUpdateEmail: updateBouncedEmailEntityEmail.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateEmailModal);
